import React, { useState, useEffect, useRef } from 'react';
import {
  FaPlane,
  FaPlaneDeparture,
  FaPlaneArrival,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
  FaClock,
  FaCalendarAlt,
  FaSearch,
  FaCamera,
  FaUpload,
  FaTicketAlt,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';
import {
  getFlights,
  addFlight,
  updateFlight,
  deleteFlight,
  uploadFlightTicket,
  getFlightTicketUrl,
  searchAirports,
  scanTicket,
  lookupFlight,
  regenerateWithFlights
} from '../api/client';
import BookingOptions from './BookingOptions';
import PageGuide from './PageGuide';

const FlightManager = ({ requestId, isOwner = true, startDate, endDate, onFlightsChange, startLocation, endLocation }) => {
  const [flights, setFlights] = useState({ outbound: [], return_flights: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState(null); // 'scan', 'lookup', 'manual'
  const [editingFlight, setEditingFlight] = useState(null);
  const [expandedFlights, setExpandedFlights] = useState({});
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);
  const [regenerateStep, setRegenerateStep] = useState(0);
  const [regenerateSteps] = useState([
    { label: 'Analyzing flight schedule', icon: FaPlane },
    { label: 'Calculating arrival times', icon: FaClock },
    { label: 'Optimizing daily activities', icon: FaCalendarAlt },
    { label: 'Generating new itinerary', icon: FaEdit },
    { label: 'Finalizing recommendations', icon: FaCheckCircle }
  ]);

  useEffect(() => {
    if (requestId) {
      fetchFlights();
    }
  }, [requestId]);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      const data = await getFlights(requestId);
      setFlights(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching flights:', err);
      setError('Failed to load flights');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFlight = async (flightData) => {
    try {
      await addFlight(requestId, flightData);
      await fetchFlights();
      setShowAddModal(false);
      setAddMethod(null);
      if (onFlightsChange) onFlightsChange();
    } catch (err) {
      console.error('Error adding flight:', err);
      throw err;
    }
  };

  const handleUpdateFlight = async (flightId, flightData) => {
    try {
      await updateFlight(requestId, flightId, flightData);
      await fetchFlights();
      setEditingFlight(null);
      if (onFlightsChange) onFlightsChange();
    } catch (err) {
      console.error('Error updating flight:', err);
      throw err;
    }
  };

  const handleDeleteFlight = async (flightId) => {
    if (!window.confirm('Are you sure you want to delete this flight?')) return;

    try {
      await deleteFlight(requestId, flightId);
      await fetchFlights();
      if (onFlightsChange) onFlightsChange();
    } catch (err) {
      console.error('Error deleting flight:', err);
      setError('Failed to delete flight');
    }
  };

  const handleRegenerateItinerary = async () => {
    if (!window.confirm('This will regenerate your itinerary to incorporate your flight times. Your current itinerary will be replaced. Continue?')) {
      return;
    }

    setIsRegenerating(true);
    setError(null);
    setRegenerateSuccess(false);
    setRegenerateStep(0);

    // Simulate progress steps while API call is running
    const stepInterval = setInterval(() => {
      setRegenerateStep(prev => {
        if (prev < 4) return prev + 1;
        return prev;
      });
    }, 3000);

    try {
      await regenerateWithFlights(requestId);
      clearInterval(stepInterval);
      setRegenerateStep(5); // Completed
      setRegenerateSuccess(true);
      if (onFlightsChange) onFlightsChange();

      // Close progress and show success after brief delay
      setTimeout(() => {
        setIsRegenerating(false);
        setRegenerateStep(0);
      }, 1500);

      // Hide success message after 5 seconds
      setTimeout(() => setRegenerateSuccess(false), 5000);
    } catch (err) {
      clearInterval(stepInterval);
      console.error('Error regenerating itinerary:', err);
      setError(err.response?.data?.detail || 'Failed to regenerate itinerary');
      setIsRegenerating(false);
      setRegenerateStep(0);
    }
  };

  const toggleFlightExpanded = (flightId) => {
    setExpandedFlights(prev => ({
      ...prev,
      [flightId]: !prev[flightId]
    }));
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (date, time) => {
    if (!date) return '';
    const dateObj = new Date(date + 'T' + (time || '00:00'));
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-4xl text-primary-500" />
      </div>
    );
  }

  const hasFlights = flights.outbound.length > 0 || flights.return_flights.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FaPlane className="text-2xl text-sky-500" />
          <h2 className="text-2xl font-bold text-neutral-800">Flight Information</h2>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 transition-colors"
            >
              <FaPlus />
              Add Flight
            </button>
          )}
          <PageGuide pageKey="flights" />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {regenerateSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <FaCheckCircle className="text-green-500" />
          <span className="text-green-700">Itinerary successfully updated with your flight times!</span>
        </div>
      )}

      {/* Regenerate Button - Show when there are flights */}
      {hasFlights && isOwner && (
        <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-neutral-800">Optimize for Your Flights</h4>
            <p className="text-sm text-neutral-600">
              Regenerate your itinerary to match your flight arrival and departure times
            </p>
          </div>
          <button
            onClick={handleRegenerateItinerary}
            disabled={isRegenerating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isRegenerating
                ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                : 'bg-sky-500 text-white hover:bg-sky-600'
            }`}
          >
            {isRegenerating ? (
              <>
                <FaSpinner className="animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <FaPlane />
                Optimize Itinerary
              </>
            )}
          </button>
        </div>
      )}

      {!hasFlights ? (
        <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-8 text-center">
          <FaPlane className="text-5xl text-sky-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Flights Added Yet</h3>
          <p className="text-neutral-500 mb-4">
            Add your flight details to optimize your itinerary based on arrival and departure times.
          </p>
          {isOwner && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 transition-colors"
            >
              <FaPlus />
              Add Your First Flight
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Outbound Flights */}
          {flights.outbound.length > 0 && (
            <FlightSection
              title="Outbound Flight"
              icon={<FaPlaneDeparture className="text-green-500" />}
              flights={flights.outbound}
              expanded={expandedFlights}
              onToggle={toggleFlightExpanded}
              onEdit={isOwner ? setEditingFlight : null}
              onDelete={isOwner ? handleDeleteFlight : null}
              formatDuration={formatDuration}
              formatDateTime={formatDateTime}
              requestId={requestId}
            />
          )}

          {/* Return Flights */}
          {flights.return_flights.length > 0 && (
            <FlightSection
              title="Return Flight"
              icon={<FaPlaneArrival className="text-orange-500" />}
              flights={flights.return_flights}
              expanded={expandedFlights}
              onToggle={toggleFlightExpanded}
              onEdit={isOwner ? setEditingFlight : null}
              onDelete={isOwner ? handleDeleteFlight : null}
              formatDuration={formatDuration}
              formatDateTime={formatDateTime}
              requestId={requestId}
            />
          )}
        </div>
      )}

      {/* Flight search — outside the empty state on purpose. It used to render
          only when no flight had been logged, so it vanished the moment a user
          added one, which is exactly when they still need the return leg or a
          seat for another traveller in the group. */}
      {startLocation && endLocation && (
        <div className="mt-4 pt-4 border-t border-neutral-100">
          <p className="text-xs text-neutral-400 mb-2">
            {hasFlights ? 'Need another leg? Search for flights online:' : 'Or search for flights online:'}
          </p>
          <BookingOptions
            type="flight"
            origin={startLocation}
            destination={endLocation}
            departDate={startDate}
            returnDate={endDate}
            surface={requestId ? `itin_${requestId}_flights` : undefined}
          />
          <p className="mt-2 text-xs text-neutral-400">
            We may earn a commission from bookings made through these links, at no extra cost to you.
          </p>
        </div>
      )}

      {/* Add Flight Modal */}
      {showAddModal && (
        <AddFlightModal
          onClose={() => {
            setShowAddModal(false);
            setAddMethod(null);
          }}
          onAdd={handleAddFlight}
          method={addMethod}
          setMethod={setAddMethod}
          startDate={startDate}
          endDate={endDate}
          existingFlights={flights}
          requestId={requestId}
        />
      )}

      {/* Edit Flight Modal */}
      {editingFlight && (
        <EditFlightModal
          flight={editingFlight}
          onClose={() => setEditingFlight(null)}
          onSave={handleUpdateFlight}
        />
      )}

      {/* Regeneration Progress Modal */}
      {isRegenerating && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-sky-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <FaPlane className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-neutral-800 mb-2">
                Optimizing Your Itinerary
              </h3>
              <p className="text-neutral-500">
                Adjusting activities around your flight schedule...
              </p>
            </div>

            <div className="space-y-4">
              {regenerateSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === regenerateStep;
                const isCompleted = index < regenerateStep;
                const isPending = index > regenerateStep;

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300 ${
                      isActive ? 'bg-sky-50 border border-sky-200' :
                      isCompleted ? 'bg-green-50' :
                      'bg-neutral-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isActive ? 'bg-sky-500 text-white animate-pulse' :
                      isCompleted ? 'bg-green-500 text-white' :
                      'bg-neutral-200 text-neutral-400'
                    }`}>
                      {isCompleted ? (
                        <FaCheckCircle className="text-lg" />
                      ) : (
                        <StepIcon className={`text-lg ${isActive ? 'animate-bounce' : ''}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        isActive ? 'text-sky-700' :
                        isCompleted ? 'text-green-700' :
                        'text-neutral-400'
                      }`}>
                        {step.label}
                      </p>
                    </div>
                    {isActive && (
                      <FaSpinner className="text-sky-500 animate-spin" />
                    )}
                  </div>
                );
              })}
            </div>

            {regenerateStep === 5 && (
              <div className="mt-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FaCheckCircle className="text-green-500 text-3xl" />
                </div>
                <p className="text-green-600 font-medium">Itinerary updated successfully!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Flight Section Component (Outbound or Return)
const FlightSection = ({
  title,
  icon,
  flights,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  formatDuration,
  formatDateTime,
  requestId
}) => {
  // Calculate total duration including layovers
  const totalDuration = flights.reduce((acc, f) => acc + (f.duration_minutes || 0), 0);

  // Calculate layover times
  const layovers = [];
  for (let i = 0; i < flights.length - 1; i++) {
    const current = flights[i];
    const next = flights[i + 1];
    if (current.arrival_date && current.arrival_time && next.departure_date && next.departure_time) {
      const arrivalTime = new Date(`${current.arrival_date}T${current.arrival_time}`);
      const departureTime = new Date(`${next.departure_date}T${next.departure_time}`);
      const layoverMinutes = Math.round((departureTime - arrivalTime) / (1000 * 60));
      layovers.push({
        airport: current.arrival_airport_iata,
        city: current.arrival_city,
        duration: layoverMinutes
      });
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-medium overflow-hidden">
      <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
            {flights.length > 1 && (
              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                {flights.length - 1} stop{flights.length > 2 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="text-sm text-neutral-500">
            {totalDuration > 0 && (
              <span className="flex items-center gap-1">
                <FaClock />
                {formatDuration(totalDuration)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="divide-y divide-neutral-100">
        {flights.map((flight, index) => (
          <React.Fragment key={flight.id}>
            <FlightCard
              flight={flight}
              isExpanded={expanded[flight.id]}
              onToggle={() => onToggle(flight.id)}
              onEdit={onEdit}
              onDelete={onDelete}
              formatDuration={formatDuration}
              formatDateTime={formatDateTime}
              requestId={requestId}
              legNumber={flights.length > 1 ? index + 1 : null}
            />
            {/* Layover indicator */}
            {layovers[index] && (
              <div className="bg-amber-50 px-6 py-3 flex items-center justify-center gap-3">
                <div className="h-px flex-1 bg-amber-200"></div>
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <FaClock />
                  <span>
                    {formatDuration(layovers[index].duration)} layover in {layovers[index].city || layovers[index].airport}
                  </span>
                </div>
                <div className="h-px flex-1 bg-amber-200"></div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Individual Flight Card
const FlightCard = ({
  flight,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  formatDuration,
  formatDateTime,
  requestId,
  legNumber
}) => {
  return (
    <div className="p-4 hover:bg-neutral-50 transition-colors">
      {/* Main flight info row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6 flex-1">
          {/* Leg number if multi-leg */}
          {legNumber && (
            <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
              {legNumber}
            </div>
          )}

          {/* Departure */}
          <div className="text-center min-w-[80px]">
            <p className="text-2xl font-bold text-neutral-800">{flight.departure_time?.slice(0, 5) || '--:--'}</p>
            <p className="text-lg font-semibold text-neutral-600">{flight.departure_airport_iata}</p>
            <p className="text-xs text-neutral-400">{formatDateTime(flight.departure_date)}</p>
          </div>

          {/* Flight line */}
          <div className="flex-1 flex items-center gap-2 px-4">
            <div className="h-px flex-1 bg-neutral-300"></div>
            <div className="flex flex-col items-center">
              <FaPlane className="text-sky-500 transform rotate-90" />
              {flight.duration_minutes && (
                <span className="text-xs text-neutral-500 mt-1">{formatDuration(flight.duration_minutes)}</span>
              )}
            </div>
            <div className="h-px flex-1 bg-neutral-300"></div>
          </div>

          {/* Arrival */}
          <div className="text-center min-w-[80px]">
            <p className="text-2xl font-bold text-neutral-800">{flight.arrival_time?.slice(0, 5) || '--:--'}</p>
            <p className="text-lg font-semibold text-neutral-600">{flight.arrival_airport_iata}</p>
            <p className="text-xs text-neutral-400">{formatDateTime(flight.arrival_date)}</p>
          </div>
        </div>

        {/* Airline info */}
        <div className="text-right ml-6 min-w-[120px]">
          {flight.airline && (
            <p className="font-medium text-neutral-700">{flight.airline}</p>
          )}
          {flight.flight_number && (
            <p className="text-sm text-neutral-500">{flight.flight_number}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onToggle}
            className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(flight)}
              className="p-2 text-sky-500 hover:text-sky-600 transition-colors"
            >
              <FaEdit />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(flight.id)}
              className="p-2 text-red-400 hover:text-red-500 transition-colors"
            >
              <FaTrash />
            </button>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-2 md:grid-cols-4 gap-4">
          {flight.departure_airport_name && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Departure Airport</p>
              <p className="text-sm text-neutral-700">{flight.departure_airport_name}</p>
              {flight.departure_terminal && (
                <p className="text-xs text-neutral-500">Terminal {flight.departure_terminal}</p>
              )}
            </div>
          )}
          {flight.arrival_airport_name && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Arrival Airport</p>
              <p className="text-sm text-neutral-700">{flight.arrival_airport_name}</p>
              {flight.arrival_terminal && (
                <p className="text-xs text-neutral-500">Terminal {flight.arrival_terminal}</p>
              )}
            </div>
          )}
          {flight.confirmation_code && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Confirmation</p>
              <p className="text-sm font-mono text-neutral-700">{flight.confirmation_code}</p>
            </div>
          )}
          {flight.cabin_class && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Cabin Class</p>
              <p className="text-sm text-neutral-700">{flight.cabin_class}</p>
            </div>
          )}
          {flight.seat_number && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Seat</p>
              <p className="text-sm text-neutral-700">{flight.seat_number}</p>
            </div>
          )}
          {flight.aircraft_type && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Aircraft</p>
              <p className="text-sm text-neutral-700">{flight.aircraft_type}</p>
            </div>
          )}
          {flight.ticket_filename && (
            <div>
              <p className="text-xs text-neutral-400 uppercase">Ticket</p>
              <a
                href={getFlightTicketUrl(requestId, flight.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700"
              >
                <FaDownload />
                {flight.ticket_filename}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to check for duplicate flight numbers
const checkDuplicateFlight = (flightNumber, flightType, existingFlights) => {
  if (!flightNumber || !existingFlights) return null;

  const normalizedNumber = flightNumber.toUpperCase().trim();
  const flightsToCheck = flightType === 'return' ? existingFlights.return_flights : existingFlights.outbound;

  const duplicate = flightsToCheck?.find(f =>
    f.flight_number?.toUpperCase().trim() === normalizedNumber
  );

  if (duplicate) {
    return `Flight ${normalizedNumber} is already added to your ${flightType === 'return' ? 'return' : 'outbound'} flights.`;
  }
  return null;
};

// Add Flight Modal
const AddFlightModal = ({ onClose, onAdd, method, setMethod, startDate, endDate, existingFlights, requestId }) => {
  const [step, setStep] = useState(method ? 'input' : 'choose');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [scannedData, setScannedData] = useState(null);

  useEffect(() => {
    if (method) {
      setStep('input');
    }
  }, [method]);

  const handleMethodSelect = (m) => {
    setMethod(m);
    setStep('input');
  };

  const renderMethodSelection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <button
        onClick={() => handleMethodSelect('scan')}
        className="p-6 border-2 border-neutral-200 rounded-xl hover:border-sky-400 hover:bg-sky-50 transition-all text-center group"
      >
        <FaCamera className="text-4xl text-sky-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h4 className="font-semibold text-neutral-800 mb-2">Scan Ticket</h4>
        <p className="text-sm text-neutral-500">Upload a photo or PDF of your ticket</p>
      </button>

      <button
        onClick={() => handleMethodSelect('lookup')}
        className="p-6 border-2 border-neutral-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all text-center group"
      >
        <FaSearch className="text-4xl text-green-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h4 className="font-semibold text-neutral-800 mb-2">Flight Number</h4>
        <p className="text-sm text-neutral-500">Enter your flight number to auto-fill details</p>
      </button>

      <button
        onClick={() => handleMethodSelect('manual')}
        className="p-6 border-2 border-neutral-200 rounded-xl hover:border-amber-400 hover:bg-amber-50 transition-all text-center group"
      >
        <FaEdit className="text-4xl text-amber-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h4 className="font-semibold text-neutral-800 mb-2">Manual Entry</h4>
        <p className="text-sm text-neutral-500">Enter flight details manually</p>
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-neutral-800 flex items-center gap-2">
            <FaPlane className="text-sky-500" />
            Add Flight
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <FaExclamationTriangle className="text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {step === 'choose' && renderMethodSelection()}

          {step === 'input' && method === 'scan' && (
            <TicketScanInput
              onScan={async (file) => {
                setIsProcessing(true);
                setError(null);
                try {
                  const result = await scanTicket(requestId, file);
                  if (result.success && result.flights && result.flights.length > 0) {
                    setScannedData(result.flights);
                    setStep('confirm');
                  } else {
                    setError(result.error || 'Could not extract flight information from the ticket. Please try manual entry.');
                  }
                } catch (err) {
                  console.error('Scan error:', err);
                  setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to scan ticket. Please try again.');
                } finally {
                  setIsProcessing(false);
                }
              }}
              isProcessing={isProcessing}
              onBack={() => {
                setMethod(null);
                setStep('choose');
              }}
            />
          )}

          {step === 'input' && method === 'lookup' && (
            <FlightLookupInput
              onLookup={async (flightNumber, date) => {
                setIsProcessing(true);
                setError(null);
                try {
                  const result = await lookupFlight(flightNumber, date);
                  if (result.found) {
                    // Full flight details found
                    setScannedData([{
                      flight_type: 'outbound',
                      departure_airport_iata: result.departure_airport_iata,
                      departure_date: result.departure_date,
                      departure_time: result.departure_time,
                      arrival_airport_iata: result.arrival_airport_iata,
                      arrival_date: result.arrival_date,
                      arrival_time: result.arrival_time,
                      airline: result.airline,
                      flight_number: result.flight_number
                    }]);
                    setStep('confirm');
                  } else if (result.airline) {
                    // Airline detected but no full details - switch to manual with pre-fill
                    setError(result.error || `Detected ${result.airline}. Switching to manual entry.`);
                    // Store the partial data for manual form
                    setScannedData([{
                      flight_type: 'outbound',
                      airline: result.airline,
                      flight_number: result.flight_number || flightNumber,
                      departure_date: date
                    }]);
                    setTimeout(() => {
                      setMethod('manual');
                      setError(null);
                    }, 2000);
                  } else {
                    setError(result.error || 'Flight not found. Please check the flight number and date.');
                  }
                } catch (err) {
                  console.error('Lookup error:', err);
                  // Handle FastAPI validation errors (array of objects) vs string errors
                  let errorMessage = 'Failed to lookup flight. Please try again.';
                  if (err.response?.data?.detail) {
                    if (Array.isArray(err.response.data.detail)) {
                      // FastAPI validation error - extract first error message
                      const firstError = err.response.data.detail[0];
                      if (firstError?.msg) {
                        errorMessage = firstError.msg;
                        // Check if it's a max length error for flight number
                        if (firstError.loc?.includes('flight_number') && firstError.type === 'string_too_long') {
                          errorMessage = 'Please enter only one flight number at a time. Use "Add Connecting Flight" for multi-leg journeys.';
                        }
                      }
                    } else {
                      errorMessage = err.response.data.detail;
                    }
                  }
                  setError(errorMessage);
                } finally {
                  setIsProcessing(false);
                }
              }}
              isProcessing={isProcessing}
              onBack={() => {
                setMethod(null);
                setStep('choose');
              }}
              startDate={startDate}
              endDate={endDate}
            />
          )}

          {step === 'input' && method === 'manual' && (
            <ManualFlightInput
              onSubmit={async (data) => {
                setIsProcessing(true);
                setError(null);
                try {
                  // Handle both single flight and array of flights
                  const flights = Array.isArray(data) ? data : [data];

                  // Check for duplicates before adding
                  for (const flight of flights) {
                    const duplicateError = checkDuplicateFlight(flight.flight_number, flight.flight_type, existingFlights);
                    if (duplicateError) {
                      setError(duplicateError);
                      setIsProcessing(false);
                      return;
                    }
                  }

                  for (const flight of flights) {
                    await onAdd(flight);
                  }
                } catch (err) {
                  setError(err.response?.data?.detail || 'Failed to add flight');
                  setIsProcessing(false);
                }
              }}
              isProcessing={isProcessing}
              onBack={() => {
                setMethod(null);
                setStep('choose');
                setScannedData(null);
              }}
              startDate={startDate}
              endDate={endDate}
              initialData={scannedData?.[0] || null}
              existingFlights={existingFlights}
            />
          )}

          {step === 'confirm' && scannedData && (
            <FlightConfirmation
              flights={scannedData}
              onConfirm={async (flights) => {
                setIsProcessing(true);
                setError(null);
                try {
                  // Check for duplicates before adding
                  for (const flight of flights) {
                    const duplicateError = checkDuplicateFlight(flight.flight_number, flight.flight_type, existingFlights);
                    if (duplicateError) {
                      setError(duplicateError);
                      setIsProcessing(false);
                      return;
                    }
                  }

                  for (const flight of flights) {
                    await onAdd(flight);
                  }
                } catch (err) {
                  setError(err.response?.data?.detail || 'Failed to add flight');
                  setIsProcessing(false);
                }
              }}
              isProcessing={isProcessing}
              onBack={() => {
                setScannedData(null);
                setStep('input');
              }}
              existingFlights={existingFlights}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Ticket Scan Input Component
const TicketScanInput = ({ onScan, isProcessing, onBack }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive ? 'border-sky-400 bg-sky-50' : 'border-neutral-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {selectedFile ? (
          <div className="space-y-4">
            <FaCheckCircle className="text-4xl text-green-500 mx-auto" />
            <p className="font-medium text-neutral-700">{selectedFile.name}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setSelectedFile(null)}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
              >
                Change File
              </button>
              <button
                onClick={() => onScan(selectedFile)}
                disabled={isProcessing}
                className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <FaCamera />
                    Scan Ticket
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <>
            <FaUpload className="text-4xl text-neutral-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-700 mb-2">
              Drag & drop your ticket here
            </p>
            <p className="text-sm text-neutral-500 mb-4">
              Supports images and PDF files
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600"
            >
              Browse Files
            </button>
          </>
        )}
      </div>

      <button
        onClick={onBack}
        className="text-neutral-500 hover:text-neutral-700 text-sm"
      >
        Back to options
      </button>
    </div>
  );
};

// Flight Lookup Input Component
const FlightLookupInput = ({ onLookup, isProcessing, onBack, startDate, endDate }) => {
  const [flightNumber, setFlightNumber] = useState('');
  const [date, setDate] = useState(startDate || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (flightNumber && date) {
      onLookup(flightNumber, date);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Flight Number
        </label>
        <input
          type="text"
          value={flightNumber}
          onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
          placeholder="e.g., UA123"
          className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Flight Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          min={startDate}
          max={endDate}
          className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          required
        />
      </div>

      <div className="flex gap-3 justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-neutral-500 hover:text-neutral-700"
        >
          Back to options
        </button>
        <button
          type="submit"
          disabled={isProcessing || !flightNumber || !date}
          className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <FaSpinner className="animate-spin" />
              Looking up...
            </>
          ) : (
            <>
              <FaSearch />
              Look Up Flight
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Manual Flight Input Component
const ManualFlightInput = ({ onSubmit, isProcessing, onBack, startDate, endDate, initialData, existingFlights }) => {
  const createEmptyLeg = (legNum, prevLeg = null) => ({
    flight_type: initialData?.flight_type || 'outbound',
    leg_sequence: legNum,
    departure_airport_iata: prevLeg?.arrival_airport_iata || '',
    departure_airport_display: prevLeg?.arrival_airport_display || '',
    departure_date: prevLeg?.arrival_date || initialData?.departure_date || startDate || '',
    departure_time: '',
    arrival_airport_iata: '',
    arrival_airport_display: '',
    arrival_date: prevLeg?.arrival_date || initialData?.departure_date || startDate || '',
    arrival_time: '',
    airline: initialData?.airline || '',
    flight_number: '',
    confirmation_code: '',
  });

  const [flightType, setFlightType] = useState(initialData?.flight_type || 'outbound');
  const [legs, setLegs] = useState([{
    ...createEmptyLeg(1),
    flight_number: initialData?.flight_number || '',
    airline: initialData?.airline || '',
  }]);
  const [activeLeg, setActiveLeg] = useState(0);
  const [airportSuggestions, setAirportSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState({ field: null, legIndex: null });
  const [duplicateWarnings, setDuplicateWarnings] = useState({});
  const searchTimeout = useRef(null);

  // Check for duplicate flight numbers when legs or flightType changes
  useEffect(() => {
    const warnings = {};
    legs.forEach((leg, index) => {
      if (leg.flight_number) {
        const duplicateError = checkDuplicateFlight(leg.flight_number, flightType, existingFlights);
        if (duplicateError) {
          warnings[index] = duplicateError;
        }
      }
    });
    setDuplicateWarnings(warnings);
  }, [legs, flightType, existingFlights]);

  const handleAirportSearch = async (query, legIndex, field) => {
    if (query.length < 2) {
      setAirportSuggestions([]);
      return;
    }

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchAirports(query);
        setAirportSuggestions(result.airports || []);
        setShowSuggestions({ field, legIndex });
      } catch (err) {
        console.error('Airport search error:', err);
      }
    }, 300);
  };

  const selectAirport = (airport, legIndex, field) => {
    const updatedLegs = [...legs];
    if (field === 'departure') {
      updatedLegs[legIndex].departure_airport_iata = airport.iata;
      updatedLegs[legIndex].departure_airport_display = `${airport.iata} - ${airport.name}`;
    } else {
      updatedLegs[legIndex].arrival_airport_iata = airport.iata;
      updatedLegs[legIndex].arrival_airport_display = `${airport.iata} - ${airport.name}`;
      // Auto-fill next leg's departure if exists
      if (legIndex < legs.length - 1) {
        updatedLegs[legIndex + 1].departure_airport_iata = airport.iata;
        updatedLegs[legIndex + 1].departure_airport_display = `${airport.iata} - ${airport.name}`;
      }
    }
    setLegs(updatedLegs);
    setShowSuggestions({ field: null, legIndex: null });
    setAirportSuggestions([]);
  };

  const updateLeg = (legIndex, field, value) => {
    const updatedLegs = [...legs];
    updatedLegs[legIndex][field] = value;
    setLegs(updatedLegs);
  };

  // State for tracking flight lookups
  const [lookingUpFlight, setLookingUpFlight] = useState({});
  const flightLookupTimeout = useRef({});

  // Auto-lookup flight details when flight number is entered
  const handleFlightNumberChange = async (legIndex, value) => {
    const upperValue = value.toUpperCase();
    updateLeg(legIndex, 'flight_number', upperValue);

    // Clear any pending lookup for this leg
    if (flightLookupTimeout.current[legIndex]) {
      clearTimeout(flightLookupTimeout.current[legIndex]);
    }

    // Only lookup if we have a valid-looking flight number (2+ letters followed by numbers)
    const flightNumberPattern = /^[A-Z]{2}\d{1,4}$/;
    if (!flightNumberPattern.test(upperValue)) {
      return;
    }

    const leg = legs[legIndex];
    const departureDate = leg.departure_date;

    // Only auto-lookup if we have a departure date
    if (!departureDate) {
      return;
    }

    // Debounce the lookup
    flightLookupTimeout.current[legIndex] = setTimeout(async () => {
      setLookingUpFlight(prev => ({ ...prev, [legIndex]: true }));

      try {
        const result = await lookupFlight(upperValue, departureDate);

        if (result.found) {
          // Auto-populate the fields from lookup result
          const updatedLegs = [...legs];
          const currentLeg = updatedLegs[legIndex];

          if (result.departure_airport_iata && !currentLeg.departure_airport_iata) {
            currentLeg.departure_airport_iata = result.departure_airport_iata;
            currentLeg.departure_airport_display = `${result.departure_airport_iata} - ${result.departure_airport_name || ''}`;
          }
          if (result.arrival_airport_iata && !currentLeg.arrival_airport_iata) {
            currentLeg.arrival_airport_iata = result.arrival_airport_iata;
            currentLeg.arrival_airport_display = `${result.arrival_airport_iata} - ${result.arrival_airport_name || ''}`;
          }
          if (result.departure_time && !currentLeg.departure_time) {
            currentLeg.departure_time = result.departure_time;
          }
          if (result.arrival_time && !currentLeg.arrival_time) {
            currentLeg.arrival_time = result.arrival_time;
          }
          if (result.arrival_date && !currentLeg.arrival_date) {
            currentLeg.arrival_date = result.arrival_date;
          }
          if (result.airline && !currentLeg.airline) {
            currentLeg.airline = result.airline;
          }

          setLegs(updatedLegs);
        } else if (result.airline) {
          // At least populate the airline name if detected
          const updatedLegs = [...legs];
          if (!updatedLegs[legIndex].airline) {
            updatedLegs[legIndex].airline = result.airline;
            setLegs(updatedLegs);
          }
        }
      } catch (err) {
        // Silently fail - user can still enter manually
        console.log('Flight lookup failed:', err);
      } finally {
        setLookingUpFlight(prev => ({ ...prev, [legIndex]: false }));
      }
    }, 500);
  };

  const addConnectingFlight = () => {
    const prevLeg = legs[legs.length - 1];
    setLegs([...legs, createEmptyLeg(legs.length + 1, prevLeg)]);
    setActiveLeg(legs.length);
  };

  const removeLeg = (index) => {
    if (legs.length <= 1) return;
    const updatedLegs = legs.filter((_, i) => i !== index);
    // Renumber remaining legs
    updatedLegs.forEach((leg, i) => {
      leg.leg_sequence = i + 1;
    });
    setLegs(updatedLegs);
    setActiveLeg(Math.min(activeLeg, updatedLegs.length - 1));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Submit all legs with proper flight_type
    const flightsToSubmit = legs.map(leg => ({
      ...leg,
      flight_type: flightType,
    }));
    onSubmit(flightsToSubmit);
  };

  const isLegValid = (leg) => {
    return leg.departure_airport_iata && leg.arrival_airport_iata &&
           leg.departure_date && leg.departure_time &&
           leg.arrival_date && leg.arrival_time;
  };

  const allLegsValid = legs.every(isLegValid);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Flight Type */}
      <div className="flex gap-4 mb-4">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="flight_type"
            value="outbound"
            checked={flightType === 'outbound'}
            onChange={(e) => setFlightType(e.target.value)}
            className="text-sky-500 focus:ring-sky-500"
          />
          <span className="text-neutral-700">Outbound</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="flight_type"
            value="return"
            checked={flightType === 'return'}
            onChange={(e) => setFlightType(e.target.value)}
            className="text-sky-500 focus:ring-sky-500"
          />
          <span className="text-neutral-700">Return</span>
        </label>
      </div>

      {/* Flight Legs Summary */}
      {legs.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {legs.map((leg, index) => (
            <React.Fragment key={index}>
              <button
                type="button"
                onClick={() => setActiveLeg(index)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  activeLeg === index
                    ? 'bg-sky-500 text-white'
                    : isLegValid(leg)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                {leg.departure_airport_iata || '???'} → {leg.arrival_airport_iata || '???'}
              </button>
              {index < legs.length - 1 && (
                <FaPlane className="text-neutral-400 text-xs" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Active Leg Form */}
      {legs.map((leg, legIndex) => (
        <div key={legIndex} className={legIndex === activeLeg ? '' : 'hidden'}>
          {legs.length > 1 && (
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-neutral-200">
              <h4 className="font-medium text-neutral-700">
                Flight {legIndex + 1} of {legs.length}
                {legIndex === 0 && ' (First Leg)'}
                {legIndex === legs.length - 1 && legIndex > 0 && ' (Final Leg)'}
                {legIndex > 0 && legIndex < legs.length - 1 && ' (Connection)'}
              </h4>
              {legs.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLeg(legIndex)}
                  className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1"
                >
                  <FaTrash className="text-xs" /> Remove
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Departure Airport */}
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                From *
              </label>
              <input
                type="text"
                value={leg.departure_airport_display || ''}
                onChange={(e) => {
                  updateLeg(legIndex, 'departure_airport_display', e.target.value);
                  updateLeg(legIndex, 'departure_airport_iata', '');
                  handleAirportSearch(e.target.value, legIndex, 'departure');
                }}
                onFocus={() => airportSuggestions.length > 0 && setShowSuggestions({ field: 'departure', legIndex })}
                placeholder="Search airport..."
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                disabled={legIndex > 0 && legs[legIndex - 1]?.arrival_airport_iata}
              />
              {showSuggestions.field === 'departure' && showSuggestions.legIndex === legIndex && airportSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {airportSuggestions.map((airport) => (
                    <button
                      key={airport.iata}
                      type="button"
                      onClick={() => selectAirport(airport, legIndex, 'departure')}
                      className="w-full px-4 py-2 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-0"
                    >
                      <span className="font-medium">{airport.iata}</span>
                      <span className="text-neutral-500"> - {airport.name}</span>
                      <span className="text-xs text-neutral-400 block">{airport.city}, {airport.country}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Arrival Airport */}
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                To *
              </label>
              <input
                type="text"
                value={leg.arrival_airport_display || ''}
                onChange={(e) => {
                  updateLeg(legIndex, 'arrival_airport_display', e.target.value);
                  updateLeg(legIndex, 'arrival_airport_iata', '');
                  handleAirportSearch(e.target.value, legIndex, 'arrival');
                }}
                onFocus={() => airportSuggestions.length > 0 && setShowSuggestions({ field: 'arrival', legIndex })}
                placeholder="Search airport..."
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              />
              {showSuggestions.field === 'arrival' && showSuggestions.legIndex === legIndex && airportSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {airportSuggestions.map((airport) => (
                    <button
                      key={airport.iata}
                      type="button"
                      onClick={() => selectAirport(airport, legIndex, 'arrival')}
                      className="w-full px-4 py-2 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-0"
                    >
                      <span className="font-medium">{airport.iata}</span>
                      <span className="text-neutral-500"> - {airport.name}</span>
                      <span className="text-xs text-neutral-400 block">{airport.city}, {airport.country}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Departure Date & Time */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Departure Date *</label>
              <input
                type="date"
                value={leg.departure_date}
                onChange={(e) => updateLeg(legIndex, 'departure_date', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Departure Time *</label>
              <input
                type="time"
                value={leg.departure_time}
                onChange={(e) => updateLeg(legIndex, 'departure_time', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>

            {/* Arrival Date & Time */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Arrival Date *</label>
              <input
                type="date"
                value={leg.arrival_date}
                onChange={(e) => updateLeg(legIndex, 'arrival_date', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Arrival Time *</label>
              <input
                type="time"
                value={leg.arrival_time}
                onChange={(e) => updateLeg(legIndex, 'arrival_time', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>

            {/* Airline & Flight Number */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Airline</label>
              <input
                type="text"
                value={leg.airline}
                onChange={(e) => updateLeg(legIndex, 'airline', e.target.value)}
                placeholder="e.g., Qatar Airways"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Flight Number</label>
              <div className="relative">
                <input
                  type="text"
                  value={leg.flight_number}
                  onChange={(e) => handleFlightNumberChange(legIndex, e.target.value)}
                  placeholder="e.g., QR701"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                    duplicateWarnings[legIndex]
                      ? 'border-amber-400 focus:ring-amber-500 bg-amber-50'
                      : 'border-neutral-300 focus:ring-sky-500'
                  }`}
                />
                {lookingUpFlight[legIndex] && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <FaSpinner className="animate-spin text-sky-500" />
                  </div>
                )}
              </div>
              {duplicateWarnings[legIndex] && (
                <p className="mt-1 text-sm text-amber-600 flex items-center gap-1">
                  <FaExclamationTriangle className="text-xs" />
                  {duplicateWarnings[legIndex]}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Add Connecting Flight Button */}
      <div className="pt-2">
        <button
          type="button"
          onClick={addConnectingFlight}
          className="w-full py-3 border-2 border-dashed border-neutral-300 rounded-lg text-neutral-600 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-colors flex items-center justify-center gap-2"
        >
          <FaPlus />
          Add Connecting Flight (Layover)
        </button>
      </div>

      {/* Navigation between legs */}
      {legs.length > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => setActiveLeg(Math.max(0, activeLeg - 1))}
            disabled={activeLeg === 0}
            className="px-4 py-2 text-neutral-600 hover:text-neutral-800 disabled:opacity-40"
          >
            Previous Leg
          </button>
          <button
            type="button"
            onClick={() => setActiveLeg(Math.min(legs.length - 1, activeLeg + 1))}
            disabled={activeLeg === legs.length - 1}
            className="px-4 py-2 text-neutral-600 hover:text-neutral-800 disabled:opacity-40"
          >
            Next Leg
          </button>
        </div>
      )}

      <div className="flex gap-3 justify-between pt-4 border-t border-neutral-200">
        <button
          type="button"
          onClick={onBack}
          className="text-neutral-500 hover:text-neutral-700"
        >
          Back to options
        </button>
        <button
          type="submit"
          disabled={isProcessing || !allLegsValid || Object.keys(duplicateWarnings).length > 0}
          className="px-6 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <FaSpinner className="animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <FaPlus />
              Add {legs.length > 1 ? `${legs.length} Flights` : 'Flight'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Flight Confirmation Component (for scanned/looked up flights)
const FlightConfirmation = ({ flights, onConfirm, isProcessing, onBack }) => {
  const [editedFlights, setEditedFlights] = useState(flights);

  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
        <FaCheckCircle className="text-green-500 text-xl" />
        <div>
          <p className="font-medium text-green-800">Flight information detected!</p>
          <p className="text-sm text-green-600">Please verify the details below before adding.</p>
        </div>
      </div>

      {editedFlights.map((flight, index) => (
        <div key={index} className="bg-neutral-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium text-neutral-700">
              {flight.flight_type === 'return' ? 'Return' : 'Outbound'} Flight {flights.length > 1 ? `(Leg ${index + 1})` : ''}
            </span>
            {flight.airline && (
              <span className="text-sm text-neutral-500">{flight.airline} {flight.flight_number}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-neutral-400">From</p>
              <p className="font-medium">{flight.departure_airport_iata}</p>
              <p className="text-neutral-500">{flight.departure_date} {flight.departure_time}</p>
            </div>
            <div>
              <p className="text-neutral-400">To</p>
              <p className="font-medium">{flight.arrival_airport_iata}</p>
              <p className="text-neutral-500">{flight.arrival_date} {flight.arrival_time}</p>
            </div>
          </div>
        </div>
      ))}

      <div className="flex gap-3 justify-between pt-4">
        <button
          onClick={onBack}
          className="text-neutral-500 hover:text-neutral-700"
        >
          Back
        </button>
        <button
          onClick={() => onConfirm(editedFlights)}
          disabled={isProcessing}
          className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <FaSpinner className="animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <FaCheckCircle />
              Confirm & Add
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Edit Flight Modal
const EditFlightModal = ({ flight, onClose, onSave }) => {
  const [formData, setFormData] = useState({ ...flight });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    try {
      await onSave(flight.id, formData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update flight');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-elevated max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-neutral-800">Edit Flight</h3>
          <button onClick={onClose} className="p-2 text-neutral-400 hover:text-neutral-600">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Departure Date
              </label>
              <input
                type="date"
                value={formData.departure_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, departure_date: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Departure Time
              </label>
              <input
                type="time"
                value={formData.departure_time || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, departure_time: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Arrival Date
              </label>
              <input
                type="date"
                value={formData.arrival_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, arrival_date: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Arrival Time
              </label>
              <input
                type="time"
                value={formData.arrival_time || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, arrival_time: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Confirmation Code
              </label>
              <input
                type="text"
                value={formData.confirmation_code || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmation_code: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Seat Number
              </label>
              <input
                type="text"
                value={formData.seat_number || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, seat_number: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isProcessing ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FlightManager;
